import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  useTranslationContext,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SharePresetToUseRequestBody} from '../../../../types/Api';
import {UserOptionsQuery} from '../../../../types/UserOptionsQuery';

type SharePresetForUserDialogProps = RequiredTestIdProps & {
  // Bound by the parent (inside the EagGrid providers) — this dialog is portaled outside
  // them via openDialog, so it must never reach for the store/config context itself.
  onSubmit: (values: SharePresetToUseRequestBody) => Promise<void>;
  useGetUserOptionsQuery: () => UserOptionsQuery;
};

const schema = z.object({
  userIds: z.array(z.string()).min(1),
});

export function SharePresetForUserDialog(props: SharePresetForUserDialogProps) {
  // Host-supplied user-options accessor; invoked at this dialog's top level (rules of hooks).
  const {userOptions, isLoading} = props.useGetUserOptionsQuery();
  const t = useTranslationContext();

  return (
    <Form<SharePresetToUseRequestBody> onSubmit={props.onSubmit} experimentalZodSchema={schema}>
      {(control) => (
        <VStack spacing={4}>
          <FormField
            data-testid={suffixTestId('userIds', props)}
            control={control}
            type="multiChoice"
            name="userIds"
            options={userOptions ?? []}
            placeholder={t('general.presets.userShare')}
            isLoading={isLoading}
          />
          <ButtonGroup align="right">
            <Button
              data-testid={suffixTestId('discardButton', props)}
              variant="secondary"
              title={t('general.actions.discard')}
              onClick={closeCurrentDialog}
            />
            <FormButton
              data-testid={suffixTestId('submitButton', props)}
              control={control}
              type="submit"
              title={t('general.actions.share')}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
